import { useState, useRef, useCallback } from 'react';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { performGetRequestWithRetry } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import type { BootstrapSyncStatus } from './types.tsx';
import { requestStatus } from './utils.tsx';

const REQUEST_TIMEOUT = 2000;
const bootstrapSyncUrl = '/rest/internal/1.0/component/sync/compass/status';

export const useGetBootstrapSyncStatus = (pollMax = 10) => {
	const [error, setError] = useState<Error | undefined>();
	const [bootstrapSyncStatus, setBootstrapSyncStatus] = useState<string | undefined>(undefined);
	const [isSyncInProgress, setIsSyncInProgress] = useState<boolean>(false);
	const timerRef = useRef<NodeJS.Timeout>();
	const pollCount = useRef<number>(0);

	const resetRequest = useCallback(() => {
		setIsSyncInProgress(false);
		clearTimeout(timerRef.current);
		pollCount.current = 0;
	}, []);

	const handleError = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(err: any) => {
			setError(err);
			resetRequest();
			setBootstrapSyncStatus(requestStatus.ENDED);
			fireErrorAnalytics({
				meta: {
					id: 'useGetBootstrapSyncStatus',
					packageName: 'jiraProjectDirectoriesComponents',

					teamName: 'compass-lodestone',
				},
				error: err,
				sendToPrivacyUnsafeSplunk: true,
			});
		},
		[resetRequest],
	);

	const invokeRequest = useCallback(
		async (): Promise<BootstrapSyncStatus | null> =>
			performGetRequestWithRetry(bootstrapSyncUrl, { retryAttempts: 3 }),
		[],
	);

	const startPollingBootstrapSyncStatus = useCallback(() => {
		const reinitiatePoll = (status: string) => {
			if (
				!bootstrapSyncStatus ||
				bootstrapSyncStatus !== requestStatus.COMPLETED ||
				bootstrapSyncStatus !== requestStatus.ENDED
			) {
				setBootstrapSyncStatus(status);
				pollCount.current += 1;

				timerRef.current = setTimeout(startPollingBootstrapSyncStatus, REQUEST_TIMEOUT);
			} else {
				clearTimeout(timerRef.current);
			}
		};

		setIsSyncInProgress(true);

		invokeRequest()
			.then((data: BootstrapSyncStatus | null) => {
				if (pollCount.current === pollMax) {
					throw new Error('Polling attempts reached');
				}

				// If Compass is not provisioned on a site, the sync status endpoint returns a 200 with no content.
				if (!data) {
					setBootstrapSyncStatus(requestStatus.ENDED);
					resetRequest();
					return;
				}

				const { state: syncState } = data;
				switch (syncState) {
					case requestStatus.COMPLETED: {
						setBootstrapSyncStatus(requestStatus.COMPLETED);
						resetRequest();
						break;
					}
					case requestStatus.IN_PROGESS: {
						reinitiatePoll(requestStatus.IN_PROGESS);
						break;
					}
					case requestStatus.NOT_INITIATED: {
						reinitiatePoll(requestStatus.NOT_INITIATED);
						break;
					}
					default: {
						setBootstrapSyncStatus(requestStatus.ENDED);
						resetRequest();
						break;
					}
				}
			})
			.catch((err) => {
				handleError(err);
			});
	}, [invokeRequest, bootstrapSyncStatus, pollMax, resetRequest, handleError]);

	const stopPollingBootstrapSyncStatus = useCallback(() => {
		setBootstrapSyncStatus(requestStatus.ENDED);
		resetRequest();
	}, [resetRequest]);

	return {
		error,
		isSyncInProgress,
		bootstrapSyncStatus,
		startPollingBootstrapSyncStatus,
		stopPollingBootstrapSyncStatus,
	};
};
